import { Injectable, Signal, computed, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AuthService, User } from '@auth0/auth0-angular';
import { environment } from '../../../environments/environment';

/**
 * Thin wrapper around the Auth0 Angular SDK that exposes authentication state
 * and the user's Markant permission groups as Angular signals.
 */
@Injectable({ providedIn: 'root' })
export class PermissionService {
  private readonly auth = inject(AuthService);

  /** The authenticated Auth0 user, or `null`/`undefined` when logged out. */
  readonly user: Signal<User | null | undefined> = toSignal(this.auth.user$, {
    initialValue: null,
  });

  /** Whether an Auth0 session is currently active. */
  readonly isAuthenticated: Signal<boolean> = toSignal(this.auth.isAuthenticated$, {
    initialValue: false,
  });

  /** Whether the Auth0 SDK is still resolving the session on startup. */
  readonly isLoading: Signal<boolean> = toSignal(this.auth.isLoading$, {
    initialValue: true,
  });

  /**
   * The user's Markant authorization groups, read from the custom Auth0 claim.
   * Always an array (empty when the user has no groups or is logged out).
   */
  readonly groups: Signal<string[]> = computed(() => {
    const claim = this.user()?.[environment.markantGroupsClaim ?? 'markant_groups'];
    return Array.isArray(claim) ? (claim as string[]) : [];
  });

  /**
   * Returns `true` when the user owns *every* group in `requiredGroups`.
   * An empty/undefined requirement is treated as "no restriction".
   */
  hasPermission(requiredGroups: string[]): boolean {
    if (!requiredGroups?.length) {
      return true;
    }
    const owned = this.groups();
    return requiredGroups.every((group) => owned.includes(group));
  }
}
